// Angular
import { Injectable } from '@angular/core';

// Libraries
import { Chart } from 'angular-highcharts';
import { SeriesOptionsType } from 'highcharts';
import { format } from 'date-fns';
import { jsPDF } from 'jspdf';
import * as saveSvgAsPng from 'save-svg-as-png';
import { takeUntil } from 'rxjs';

// Models
import { CustomChartAttachmentResponse, CustomChartSaveAttachmentModel } from '../models/custom-chart-init-model';
import { orientation } from '../dialogs/set-attachment-options/models/types';
import { Job } from '../../../models';
import { CustomChartsInitService } from './custom-charts-init.service';
import { UtilityHelper } from '../../../../../shared/helpers';
import { AppCleanup } from '../../../../../shared/models';

// Services
import { LayoutConfigService } from '../../../../../shared/services/layout-config.service';
import objectPath from 'object-path';

@Injectable()
export class CustomChartsSaveAsAttachmentService extends AppCleanup {
    job: Job;
    defaults = {
        dimensions: {
            width: 8.5,
            height: 11,
        },
        orientation: 'landscape',
        chartStyles: {
            legendFontSize: '23px',
            xAxisLabelsStyle: {
                spacingTop: '5px',
                color: '#909090',
                fontSize: '19px',
                fontWeight: 'bold',
            },
            yAxisLabelsStyle: {
                fontSize: '19px',
                padding: '3px',
            },
        },
        dpi: 225,
        fontSize: 32,
        outerPadding: 30,
        headerLogoAspectRatio: 3.064,
        headerLogoFilePath: '',
    };
    private yAxisLabels: string[] = [];
    private headerLogo: HTMLImageElement = new Image();
    
    constructor(
        private customChartInitService: CustomChartsInitService,
        private layoutConfigService: LayoutConfigService,
    ) {
        super();
        // Initialize logo - displays in the top-right corner of the custom chart.
        
        this.customChartInitService.job$.pipe(takeUntil(this.destroy)).subscribe((job) => {
            this.job = job;
        });
        
        this.layoutConfigService.localLayoutConfig$.pipe(takeUntil(this.destroy)).subscribe((layoutConfig) => {
            if (layoutConfig) {
                this.defaults.headerLogoFilePath = objectPath.get(layoutConfig, 'self.logo.dark');
                this.headerLogo.src = this.defaults.headerLogoFilePath;
            }
        });
    }
    
    async generateCustomChartAttachment(
        data: CustomChartSaveAttachmentModel
    ): Promise<CustomChartAttachmentResponse>
    {
        this.resetServiceProperties();
        const { dpi, fontSize, headerLogoAspectRatio, outerPadding } = this.defaults;
        const { chart, chartSettings, orientation: dataOrientation, annotations } = data;
        const chartReference = chart.ref as Highcharts.Chart;
        const headerTexts: string[] = this.buildHeaderTexts(data);
        const orientation: orientation = dataOrientation || (this.defaults.orientation as orientation);
        const defaultWidth: number = this.defaults.dimensions.width;
        const defaultHeight: number = this.defaults.dimensions.height;
        
        let width: number = defaultWidth * dpi;
        let height: number = defaultHeight * dpi;
        
        if (orientation === 'landscape') {
            width = defaultHeight * dpi;
            height = defaultWidth * dpi;
        }
        
        // Hide series in the legend if the series is hidden in the chart.
        const options: SeriesOptionsType = {
            showInLegend: false,
        } as SeriesOptionsType;
        
        for (const series of chartReference.series) {
            if (!series.visible) {
                series.update(options, false);
            }
        }
        
        // Update fonts for x and y axes, and the x-axis legend.
        this.updateChartStyles(chart);
        
        for (const [index, yAxisLabel] of this.yAxisLabels.entries()) {
            chartReference.yAxis[index].setTitle({
                text: yAxisLabel,
                style: {
                    fontSize: '12 px',
                    color: this.getYAxisColorHex(chart, index),
                },
            });
        }
        
        const topPadding: number = 10;
        const headerHeight: number = (headerTexts.length + 1) * (fontSize + topPadding);
        
        const chartImage: HTMLImageElement = await this.convertChartToImage(chart, width, height, annotations, orientation);
        const canvas: HTMLCanvasElement = document.createElement('canvas');
        
        canvas.width = width;
        canvas.height = height;
        const context: CanvasRenderingContext2D = canvas.getContext('2d');
        
        context.fillStyle = 'white';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.font = `${fontSize}px poppins`;
        context.fillStyle = '#626262';
        
        // Initialize & draw header text, top-left.
        for (const [index, text] of headerTexts.entries()) {
            const y: number = outerPadding - topPadding + (index + 1) * (fontSize + topPadding);
            context.fillText(text, outerPadding, y);
        }
        
        // Initialize & draw header logo display, top-right.
        const logoHeight: number = headerHeight - 32;
        const logoWidth: number = logoHeight * headerLogoAspectRatio;
        const logoPositionX: number = canvas.width - logoWidth - outerPadding;
        context.drawImage(this.headerLogo, logoPositionX, outerPadding, logoWidth, logoHeight);
        
        // Initialize & draw canvas/chart image, bottom-center.
        const adjustedHeaderHeight: number = outerPadding + headerHeight;
        const adjustedWidth: number = width - 2 * outerPadding;
        const adjustedHeight: number = height - 2 * outerPadding - adjustedHeaderHeight + outerPadding;
        context.drawImage(chartImage, outerPadding, adjustedHeaderHeight, adjustedWidth, adjustedHeight);
        
        // Canvas to PDF conversion and file saving.
        const pdf: Blob = this.convertCanvasToPdf(canvas, orientation);
        
        // Restore series in the legend.
        options.showInLegend = true;
        for (let i: number = 0; i < chartReference.series?.length; i++) {
            if (!chartReference.series[i].visible && chartSettings.channelSettings[i].showGraph) {
                chartReference.series[i].update(options, false);
            }
        }
        
        chartReference.redraw(false);
        
        return { pdf, canvas };
    }
    
    private resetServiceProperties() {
        this.yAxisLabels = [];
    }
    
    private updateChartStyles(chart: Chart) {
        const { legendFontSize, xAxisLabelsStyle, yAxisLabelsStyle } = this.defaults.chartStyles;
        
        // Update X-Axis font styles.
        chart.ref.update({
            legend: {
                itemStyle: {
                    fontSize: legendFontSize,
                },
            },
            xAxis: {
                labels: {
                    style: xAxisLabelsStyle,
                },
            },
            annotations: [
                {
                    labels: [],
                    labelOptions: {},
                },
            ],
        });
        
        // Update Y-Axis font styles.
        const yAxisLength = chart.ref.yAxis.length;
        for (let i: number = 0; i < yAxisLength; i++) {
            chart.ref.yAxis[i].update({
                labels: {
                    style: yAxisLabelsStyle,
                },
            });
        }
    }
    
    private getYAxisColorHex(chart: Chart, index: number) {
        return chart.ref.yAxis[index]?.userOptions?.labels?.style?.color;
    }
    
    private buildHeaderTexts(data: CustomChartSaveAttachmentModel): string[] {
        let { title, startDate, endDate, companyName, wellName, wellNumber } = data;
        const dateFormat = 'M/d/yyyy HH:mm';
        let startDateFormatted: string = format(startDate, dateFormat);
        let endDateFormatted: string = format(endDate, dateFormat);
        const startAndEndDate: string = `Start Date: ${startDateFormatted}    End Date: ${endDateFormatted}`;
        let companyAndWell: string = '';
        
        // Conditionally build and add second row text all three variables are set.
        if (companyName && wellName && wellNumber) {
            companyAndWell = `${companyName} - ${wellName} ${wellNumber}`;
        }
        
        return [title, companyAndWell, startAndEndDate].filter((headerText: string) => headerText !== '');
    }
    
    private async convertChartToImage(
        chart: Chart,
        width: number,
        height: number,
        annotations?: any,
        orientation: 'landscape' | 'portrait' = 'landscape',
    ): Promise<HTMLImageElement> {
        const chartReference = chart.ref as Highcharts.Chart;
        
        if (chartReference.plotWidth === 0) {
            chartReference.setSize(width, height, false);
        }
        
        const measureDiv = document.createElement('div');
        Object.assign(measureDiv.style, {
            position: 'absolute',
            visibility: 'hidden',
            whiteSpace: 'nowrap',
            fontSize: '21px',
            fontWeight: 'bold',
        } as CSSStyleDeclaration);
        document.body.appendChild(measureDiv);
        
        // Set export options for SVG.
        const exportOptions = UtilityHelper.deepCopy(chartReference.userOptions);
        exportOptions.chart.width = width;
        exportOptions.chart.height = height;
        exportOptions.xAxis = { ...(exportOptions.xAxis || {}), ordinal: false };
        
        let svgString = chartReference.getSVG(exportOptions);
        
        // Plot for SVG.
        const svgDoc = new DOMParser().parseFromString(svgString, 'image/svg+xml');
        const plotBackground = svgDoc.querySelector('.highcharts-plot-background');
        const svgPlotLeft = plotBackground?.getAttribute('x') ? +plotBackground.getAttribute('x')! : 0;
        const svgPlotWidth = plotBackground?.getAttribute('width') ? +plotBackground.getAttribute('width')! : width;
        const svgPlotTop = plotBackground?.getAttribute('y') ? +plotBackground.getAttribute('y')! : 0;
        const svgPlotHeight = plotBackground?.getAttribute('height') ? +plotBackground.getAttribute('height')! : height;
        
        const livePlotWidth = chartReference.plotWidth;
        const livePlotHeight = chartReference.plotHeight;
        const scaleFactorX = svgPlotWidth / livePlotWidth;
        const scaleFactorY = svgPlotHeight / livePlotHeight;
        
        // Loop & build each annotation label (comment).
        if (annotations?.labels?.length) {
            const xAxis = chartReference.xAxis[0];
            const yAxis = chartReference.yAxis[0]; // Assuming first y-axis, adjust if needed
            
            // Stacking logic variables.
            const minTimeGap = 60000 * 30; // 30 minutes in milliseconds
            const processedLabels: Array<{x: number, y: number, yAxis: number, yPosition: number}> = [];
            
            annotations.labels.forEach((label: any, index: number) => {
                if (!label?.text || !label.point)
                    return;
                
                const timeStamp = label.point.x as number;
                const yValue = label.point.y as number;
                
                // Check if this label has a specific y-axis index.
                const yAxisIndex = label.point.yAxis || 0;
                const labelYAxis = chartReference.yAxis[yAxisIndex] || yAxis;
                
                // Set X-position (same for both approaches).
                const livePixelX = xAxis.toPixels(timeStamp, true);
                const xPosition = svgPlotLeft + livePixelX * scaleFactorX;
                
                // Check for stacking with previously processed labels.
                let stackLevel = 0;
                for (const processed of processedLabels) {
                    const sameYAxis = processed.yAxis === yAxisIndex;
                    const withinTimeRange = Math.abs(processed.x - timeStamp) < minTimeGap;
                    
                    // Only stack if they're on the same y-axis, within time range,
                    // and have similar y-positions (to avoid stacking comments at different heights)
                    if (sameYAxis && withinTimeRange) {
                        // Check if they would be at similar y-positions
                        const labelYMin = labelYAxis.min;
                        const labelYRange = labelYAxis.max - labelYMin;
                        const labelTenPercent = labelYMin + (labelYRange * 0.1);
                        
                        const bothAtBottom = yValue <= labelTenPercent && processed.y <= labelTenPercent;
                        const bothOnChart = yValue > labelTenPercent && processed.y > labelTenPercent;
                        
                        if (bothAtBottom || bothOnChart) {
                            stackLevel++;
                        }
                    }
                }
                
                // Determine Y-position based on y-value.
                let yPosition: number;
                
                // Use the correct y-axis for threshold calculation.
                const labelYMin = labelYAxis.min;
                const labelYMax = labelYAxis.max;
                const labelYRange = labelYMax - labelYMin;
                const labelTenPercent = labelYMin + (labelYRange * 0.1);
                
                if (yValue <= labelTenPercent) {
                    // Position along x-axis.
                    const baseY = orientation === 'landscape'
                        ? height - 140
                        : height - 160;
                    const stackOffset = stackLevel * -50; // Same stacking as live chart
                    yPosition = baseY + stackOffset;
                } else {
                    // Position at actual y-coordinate on chart.
                    // Use the label's specific y-axis for positioning.
                    const livePixelY = labelYAxis.toPixels(yValue, true);
                    
                    // Calculate the same buffer/offset as x-axis comments.
                    const yBuffer = orientation === 'landscape' ? 140 : 160;
                    const stackOffset = stackLevel * -50; // Apply stacking
                    
                    // Subtract the buffer to shift the comment up from its data point.
                    yPosition = svgPlotTop + livePixelY * scaleFactorY - (yBuffer * 0.75) + stackOffset;
                }
                
                // Store processed label info for future stacking checks.
                processedLabels.push({
                    x: timeStamp,
                    y: yValue,
                    yAxis: yAxisIndex,
                    yPosition: yPosition
                });
            });
        }
        
        document.body.removeChild(measureDiv);
        
        // Draw PNG.
        const finalSvg = new DOMParser().parseFromString(svgString, 'image/svg+xml');
        const dataUri: string = await saveSvgAsPng.svgAsPngUri(finalSvg.documentElement, { scale: 1 });
        const chartImage = new Image();
        
        await new Promise<void>(resolve => {
            //@ts-ignore
            chartImage.onload = resolve;
            chartImage.src = dataUri;
        });
        
        return chartImage;
    }
    
    private convertCanvasToPdf(canvas: HTMLCanvasElement, orientation: orientation): Blob {
        const jsPdf: jsPDF = new jsPDF({
            orientation,
            unit: 'px',
            format: [canvas.width, canvas.height],
        });
        
        jsPdf.addImage({
            imageData: canvas,
            x: 0,
            y: 0,
            width: canvas.width,
            height: canvas.height,
        });
        
        return jsPdf.output('blob');
    }
}
